import { Signal } from "signals";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import Layers from "slotworks/graphics/layers/layers";
import AutoplayModel from "slotworks/model/autoplay/autoplay-model";
import BetModel from "slotworks/model/bets/bet-model";
import SettingsModel from "slotworks/model/settings/settings-model";
import AutoSmartUIGroup from "slotworks/smart-ui/auto-smart-ui-group";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import SmartUIFlags, { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import HeadlessSmartUIGroup from "slotworks/smart-ui/smart-ui-group";
import SmartUIGroup from "slotworks/smart-ui/smart-ui-group";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";

class SmartUIManager {

    public onGroupShow: Signal;
    public onGroupHide: Signal;

    public onOptionsChanged: Signal = new Signal();

    private autoShowGroupFlagMap: SmartUIGroup[][] = [];
    private autoHideGroupFlagMap: SmartUIGroup[][] = [];

    private options: ControlOptions;

    private smartUIGroups: Map<string, SmartUIGroup> = new Map<string, SmartUIGroup>();

    private buttonLayerMap: Map<Layers, ButtonLayerMap> = new Map<Layers, ButtonLayerMap>();

    public init() {
        this.syncOptionsFromModel();

        this.onGroupShow = new Signal();
        this.onGroupHide = new Signal();

        SmartUIFlags.init();
        SmartUIFlags.onFlagsUpdated.add((bitmask: number) => this.smartUIFlagsUpdated(bitmask));
    }

    public newHeadlessSmartUIGroup(name: string): HeadlessSmartUIGroup {
        const group: HeadlessSmartUIGroup = new HeadlessSmartUIGroup(name);

        this.smartUIGroups.set(name, group);

        return group;
    }

    public newSmartUIGroup(layer: Layers, scene: string, name?: string): AutoSmartUIGroup {
        if (!name) {
            name = layer.id + "_" + scene;
        }

        const group = new AutoSmartUIGroup(name, layer, scene);

        group.onShow.add(() => this.onGroupShow.dispatch(name));
        group.onHide.add(() => this.onGroupHide.dispatch(name));

        this.smartUIGroups.set(name, group);

        return group;
    }

    public showGroup(group: SmartUIGroup): Contract<void> {
        return new Sequence([
            () => group.show(),
            () => Contract.wrap(() => {
                this.syncOptionsFromModel();
                group.updateOptions(this.options);
                this.onOptionsChanged.dispatch(this.options);
                group.addChangeListener(this.onChangeHandler);
                group.smartUIFlagsUpdated(SmartUIFlags.bitmask);
            })
        ]);
    }

    public show(group: string): Contract<void> {
        if (!this.get(group)) {
            return Contract.empty();
        }
        return this.showGroup(this.get(group));
    }

    public get(name: string): SmartUIGroup {
        return this.smartUIGroups.get(name);
    }

    public registerButtonToGroup(name: string, layer: Layers, scenes: string[], group: SmartUIGroup): void {
        if (!this.buttonLayerMap.has(layer)) {
            this.buttonLayerMap.set(layer, new ButtonLayerMap(layer, (smartUIGroup: SmartUIGroup) => this.showGroup(smartUIGroup).execute()));
        }

        scenes.forEach((scene) => this.buttonLayerMap.get(layer).addNewButton(name, scene, group));
    }

    public addAutoShowGroupCondition(group: SmartUIGroup, mustMatch: SmartUIFlag[], mustNotMatch: SmartUIFlag[] = []): void {
        this.addAutoGroupCondition(group, mustMatch, mustNotMatch, this.autoShowGroupFlagMap);

        this.refresh();
    }

    public addAutoHideGroupCondition(group: SmartUIGroup, mustMatch: SmartUIFlag[], mustNotMatch: SmartUIFlag[] = []): void {
        this.addAutoGroupCondition(group, mustMatch, mustNotMatch, this.autoHideGroupFlagMap);

        this.refresh();
    }

    public addAutoGroupCondition(group: SmartUIGroup, mustMatch: SmartUIFlag[], mustNotMatch: SmartUIFlag[] = [], targetMap: SmartUIGroup[][]): void {
        const matchMask = mustMatch.reduce((sum, val) => sum + val, 0);
        const mustNotMatchMask = mustNotMatch.reduce((sum, val) => sum + val, 0);

        for (let testMask = 0; testMask < SmartUIFlag.NEVER; testMask++) {
            const positiveConditionMet = ((testMask & matchMask) === matchMask) || mustMatch.length === 0;
            const negativeConditionMet = (testMask & mustNotMatchMask) === 0;

            if (positiveConditionMet && negativeConditionMet) {
                if (!targetMap[testMask]) {
                    targetMap[testMask] = [];
                }
                targetMap[testMask].push(group);
            }
        }
    }

    public refresh(): void {
        this.syncOptionsFromModel();
        this.onChangeHandler(this.options);
        this.smartUIFlagsUpdated(SmartUIFlags.bitmask);
    }

    private onChangeHandler = (options: ControlOptions) => {
        AutoplayModel.updateFromOptions(options);
        BetModel.updateFromOptions(options);
        SettingsModel.updateFromOptions(options);

        this.syncOptionsFromModel();

        this.smartUIGroups.forEach((group: SmartUIGroup) => {
            if (group.isVisible()) {
                group.updateOptions(this.options);
            }
        });

        this.onOptionsChanged.dispatch(this.options);
    }

    private syncOptionsFromModel(): void {
        const options = new ControlOptions();

        AutoplayModel.injectAutoplayOptions(options);
        BetModel.injectBetOptions(options);
        SettingsModel.injectGeneralOptions(options);

        this.options = options;
    }

    private smartUIFlagsUpdated(bitmask: number): void {

        this.smartUIGroups.forEach((group) => group.smartUIFlagsUpdated(bitmask));

        if (this.autoShowGroupFlagMap[bitmask]) {
            for (const group of this.autoShowGroupFlagMap[bitmask]) {
                this.showGroup(group).execute();
            }
        }

        if (this.autoHideGroupFlagMap[bitmask]) {
            for (const group of this.autoHideGroupFlagMap[bitmask]) {
                group.hide();
                group.setEnabled(false);
            }
        }

        this.buttonLayerMap.forEach((buttonLayer) => buttonLayer.smartUIFlagsUpdated());
    }
}

export default new SmartUIManager();

export class ButtonLayerMap {

    private buttonLayerScenes: ButtonSceneGroup[] = [];
    private attachedButtons: AttachedButton[] = [];
    private enabled: boolean = true;

    constructor(private layer: Layers, private buttonListener: (smartUIGroup: SmartUIGroup) => void) {
        this.layer.onSceneEnter.add(this.onSceneSwitch);
    }

    public addNewButton(name: string, scene: string, group: SmartUIGroup): void {
        if (this.layer.currentSceneIs(scene)) {
            this.attachButton(name, group);
        }

        this.buttonLayerScenes.push(new ButtonSceneGroup(name, scene, group));
    }

    public smartUIFlagsUpdated(): void {
        this.attachedButtons.forEach((attachedButton: AttachedButton) => attachedButton.button.setEnabled(attachedButton.group.isEnabled()));
    }

    public setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        this.attachedButtons.forEach((attachedButton: AttachedButton) => attachedButton.button.setEnabled(enabled));
    }

    /*
    Old button listeners are cleared out when the layer they are on is destroyed
    So I don't need to worry about removing listeners from buttons
     */
    private onSceneSwitch = (newScene: string) => {
        this.attachedButtons = [];

        this.buttonLayerScenes.forEach((value: ButtonSceneGroup) => {
            if (value.scene === newScene) {
                this.attachButton(value.button, value.group);
            }
        });
    }

    private attachButton(name: string, group: SmartUIGroup): void {
        const button: FlexiButton = this.layer.getFlexiButton(name);
        if (button) {
            group.smartUIFlagsUpdated(SmartUIFlags.bitmask);
            button.setEnabled(this.enabled);
            this.attachedButtons.push(new AttachedButton(button, group));
            button.on(ButtonEvent.CLICK, () => this.buttonListener(group));
            this.smartUIFlagsUpdated();

            if (group instanceof AutoSmartUIGroup) {
                group.attachButton(button);
            }
        }
    }
}

class AttachedButton {
    constructor(public button: FlexiButton, public group: SmartUIGroup) {
        // ..
    }
}

export class ButtonSceneGroup {
    constructor(public button: string, public scene: string, public group: SmartUIGroup) {
        // ..
    }
}
